import {
  getApprovedProductImage,
  PRODUCT_IMAGE_APPROVALS,
  PRODUCT_IMAGE_CAPTURES,
  productImageEntry,
  type ProductImageLocale,
  type ResolvedProductImage,
} from "@appcaire/shared/help/productImages";

/** Explicit local review only. Production builds always use approvals. */
export const PRODUCT_IMAGE_REVIEW =
  import.meta.env.DEV && import.meta.env.VITE_PRODUCT_IMAGE_REVIEW === "1";

export function getWebsiteProductImage(
  id: string,
  locale: ProductImageLocale,
  width?: number,
  approvedOnly = false,
): ResolvedProductImage | null {
  if (approvedOnly) return getApprovedProductImage(id, locale, width);
  if (PRODUCT_IMAGE_REVIEW) {
    const capture = PRODUCT_IMAGE_CAPTURES[id];
    if (capture) {
      const entry = productImageEntry(id);
      const rendition =
        width && width >= 1440 ? capture.renditions?.["1920"] : undefined;
      const file = rendition?.files[locale] ?? capture.files[locale];
      // Development-only filesystem URL also resolves images added after
      // the dev server starts. Production has no root and reads approvals.
      const root = import.meta.env.VITE_PRODUCT_IMAGE_REVIEW_ROOT;
      if (!root) throw new Error("Missing local product-image review root");
      const src = `/@fs${root}/${file.src}`;
      return {
        entry,
        locale,
        file: { ...file, src },
        elements:
          rendition?.elementsByLocale[locale] ??
          capture.elementsByLocale?.[locale] ??
          capture.elements,
        metrics: capture.metrics[locale] ?? {},
        text: entry.text[locale],
      };
    }
  }
  return getApprovedProductImage(id, locale, width);
}

export function productImageReviewLabel(
  id: string,
  locale: ProductImageLocale,
): string | null {
  if (!PRODUCT_IMAGE_REVIEW) return null;
  const capture = PRODUCT_IMAGE_CAPTURES[id];
  if (
    capture &&
    PRODUCT_IMAGE_APPROVALS[id]?.fingerprint === capture.fingerprint
  ) {
    return locale === "sv" ? "Godkänd aktuell bild" : "Current approved image";
  }
  return capture
    ? locale === "sv"
      ? "Opublicerat bildförslag — inväntar godkännande"
      : "Unpublished image candidate — awaiting approval"
    : locale === "sv"
      ? "Nuvarande bild — ersättningsbild återstår"
      : "Current image — replacement pending";
}

export type ApprovedProductImageFile = {
  src: string;
  width: number;
  height: number;
  alt: string;
};

/**
 * File and alt text of an owner-approved catalogue image, for slots that draw
 * their own frame (phone, laptop, backdrop). Throws when the id has no
 * approval, so a page can never fall back to an older capture.
 */
export function approvedProductImageFile(
  id: string,
  locale: ProductImageLocale,
): ApprovedProductImageFile {
  const image = getApprovedProductImage(id, locale);
  if (!image) throw new Error(`Product image "${id}" has no approval`);
  return {
    src: image.file.src,
    width: image.file.width,
    height: image.file.height,
    alt: image.text.alt,
  };
}
